import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Package, BookOpen, Layers, Palette } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import SEO from '../components/SEO';

const DEFAULT_ICON_MAP = { Package, BookOpen, Layers, Palette, CheckCircle };

const DEFAULT_INTRO = {
  label:    'What We Do',
  heading:  'Comprehensive Sample Solutions',
  body:     "From concept to delivery, House of Sample provides end-to-end sample solutions designed to elevate your brand's presentation and streamline your sales process.",
};

const DEFAULT_PROCESS = {
  label:   'How It Works',
  heading: 'Our Process',
  steps: [
    { icon: '💬', title: 'Consultation', desc: 'We understand your brand vision, requirements, and target audience to craft the perfect brief.' },
    { icon: '✏️', title: 'Design',       desc: 'Our designers create detailed mockups and prototypes tailored to your specifications.' },
    { icon: '⚙️', title: 'Production',   desc: 'Expert craftsmen bring your samples to life using premium materials and precision techniques.' },
    { icon: '🚀', title: 'Delivery',     desc: 'Quality-checked samples are carefully packaged and delivered to your doorstep on time.' },
  ],
};

const DEFAULT_CTA = {
  heading: 'Ready to Get Started?',
  body:    'Contact us today to discuss your sample requirements.',
  btnLabel:'Get in Touch',
  btnLink: '/contact',
};

export default function SampleSolution() {
  const [solutions, setSolutions] = useState([]);
  const [hero,    setHero]    = useState(null);
  const [intro,   setIntro]   = useState(DEFAULT_INTRO);
  const [process, setProcess] = useState(DEFAULT_PROCESS);
  const [cta,     setCta]     = useState(DEFAULT_CTA);
  const [loaded,  setLoaded]  = useState(false);

  useEffect(() => {
    api.get('/solutions').then(res => setSolutions(res.data.solutions)).catch(() => {});
    api.get('/content?page=solutions').then(res => {
      const items = res.data.content || [];
      const find  = s => items.find(c => c.section === s);

      const h = find('hero');
      if (h) setHero(h);

      const intr = find('intro');
      if (intr) setIntro({
        label:   intr.subtitle || DEFAULT_INTRO.label,
        heading: intr.title    || DEFAULT_INTRO.heading,
        body:    intr.body     || DEFAULT_INTRO.body,
      });

      const proc = find('process');
      if (proc) setProcess({
        label:   proc.subtitle || DEFAULT_PROCESS.label,
        heading: proc.title    || DEFAULT_PROCESS.heading,
        steps:   proc.extraData?.steps?.length ? proc.extraData.steps : DEFAULT_PROCESS.steps,
      });

      const c = find('cta');
      if (c) setCta({
        heading:  c.title                    || DEFAULT_CTA.heading,
        body:     c.body                     || DEFAULT_CTA.body,
        btnLabel: c.ctaButtons?.[0]?.label   || DEFAULT_CTA.btnLabel,
        btnLink:  c.ctaButtons?.[0]?.link    || DEFAULT_CTA.btnLink,
      });
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const heroImage = hero?.images?.[0] || hero?.image || 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80';
  const heroTitle = hero?.title || 'Sample Solutions';

  const defaultSolutions = [
    { icon: Package,  title: 'Customized Catalogue Solution', desc: 'Professional catalogues that showcase your collection with precision and elegance.',        linkedCategory: 'customized-catalogue-solution' },
    { icon: BookOpen, title: 'Sample Books',                  desc: 'Beautifully bound sample books for trade shows, showrooms, and client presentations.',       linkedCategory: '' },
    { icon: Layers,   title: 'Hangers & Waterfalls',          desc: 'Custom hangers and waterfall displays for retail and wholesale environments.',               linkedCategory: 'hangers-waterfalls' },
    { icon: Palette,  title: 'Card Folder',                   desc: 'High-quality card folders designed for convenience and crafted for impact.',                 linkedCategory: 'card-folder' },
  ];
  const displaySolutions = solutions.length > 0 ? solutions : defaultSolutions;

  return (
    <>
      <SEO title="Sample Solutions" description="Discover our comprehensive sample solutions." />

      {/* Hero */}
      <section className="relative h-[432px] flex items-end pb-16 overflow-hidden">
        {loaded && <img src={heroImage} alt="Solutions"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: hero?.extraData?.objectPosition || '50% 50%' }} />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <h1 className="font-serif text-4xl md:text-5xl text-white">{heroTitle}</h1>
        </div>
      </section>

      {/* Intro */}
      <section className="py-24 px-6 max-w-4xl mx-auto text-center">
        <p className="text-gold text-xs tracking-[0.3em] uppercase mb-4">{intro.label}</p>
        <h2 className="section-title">{intro.heading}</h2>
        <div className="gold-line" />
        <p className="text-ink-400 leading-relaxed">{intro.body}</p>
      </section>

      {/* Solutions grid */}
      <section className="pb-24 px-6 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8">
          {displaySolutions.map((sol, i) => {
            const Icon = sol.icon || DEFAULT_ICON_MAP[sol.iconName] || CheckCircle;
            // Derive category slug: use linkedCategory if set, otherwise slugify the title
            const categorySlug = sol.linkedCategory
              || (sol.title ? sol.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') : '');
            const href = categorySlug
              ? `/products?category=${encodeURIComponent(categorySlug)}&from=solutions`
              : `/products?from=solutions`;

            return (
              <motion.div key={sol._id || sol.title}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Link
                  to={href}
                  className="flex gap-6 p-8 border border-dark-600 hover:border-gold transition-colors duration-300 bg-white group cursor-pointer block">
                  <div className="shrink-0">
                    {sol.image ? (
                      <img src={sol.image} alt={sol.title} className="w-16 h-16 object-cover" />
                    ) : (
                      <div className="w-16 h-16 bg-dark-800 flex items-center justify-center">
                        <Icon className="text-gold" size={28} />
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-ink mb-3 group-hover:text-gold transition-colors duration-200">{sol.title}</h3>
                    <p className="text-ink-400 text-sm leading-relaxed">{sol.description || sol.desc}</p>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-dark-800 border-y border-dark-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="text-gold text-xs tracking-[0.4em] uppercase mb-3">{process.label}</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: 0.1 }} className="section-title">{process.heading}</motion.h2>
            <div className="gold-line" />
          </div>

          <div className="relative">
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
            <div className="grid md:grid-cols-4 gap-8 md:gap-4">
              {process.steps.map((item, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.18, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="relative text-center group">
                  <div className="relative mx-auto mb-6 w-20 h-20">
                    <motion.div initial={{ scale: 0.6, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true }} transition={{ delay: i * 0.18 + 0.2, duration: 0.5 }}
                      className="absolute inset-0 rounded-full border-2 border-gold/30 group-hover:border-gold transition-colors duration-500" />
                    <motion.div initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
                      transition={{ delay: i * 0.18 + 0.1, type: 'spring', stiffness: 200 }}
                      className="w-full h-full rounded-full bg-white border border-gold/50 flex items-center justify-center shadow-lg group-hover:shadow-gold/20 group-hover:shadow-xl transition-all duration-500">
                      <span className="text-2xl">{item.icon}</span>
                    </motion.div>
                    <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-gold flex items-center justify-center">
                      <span className="text-white text-xs font-bold">{i + 1}</span>
                    </div>
                  </div>
                  <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                    transition={{ delay: i * 0.18 + 0.35 }}>
                    <h3 className="font-serif text-xl text-ink mb-3 group-hover:text-gold transition-colors duration-300">{item.title}</h3>
                    <p className="text-ink-400 text-sm leading-relaxed px-2">{item.desc}</p>
                  </motion.div>
                  {i < process.steps.length - 1 && (
                    <motion.div initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }} transition={{ delay: i * 0.18 + 0.5 }}
                      className="hidden md:block absolute top-10 -right-4 text-gold/50 text-lg z-10">→</motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center">
        <h2 className="section-title mb-4">{cta.heading}</h2>
        <p className="text-ink-400 mb-8">{cta.body}</p>
        <Link to={cta.btnLink} className="btn-gold">{cta.btnLabel}</Link>
      </section>
    </>
  );
}
